<!--@format-->
<template>
    <div class="document-signature-widget">
        <div class="widget-header">
            <h3>Signature</h3>
            <div class="svg-container widget-close-button" @click="closeWidget">
                <widget-close-svg />
            </div>
        </div>
        <div class="content-wrapper">
            <div class="tabs-container">
                <app-tab
                    v-for="view in views"
                    :key="view.id"
                    :disabled="view.disabled"
                    :view="view"
                    :active="isActiveTab(view)"
                    size="small"
                    @click.native="setActiveTab(view)"
                />
            </div>
            <div class="signing-card-container">
                <div class="document-signing-card">
                    <div v-if="selectedTab !== 'upload'" class="settings-container">
                        <app-select-dropdown
                            v-if="selectedTab === 'text'"
                            placeholder="Select Font"
                            :value="selectedFont"
                            :is-custom-selection="true"
                            :is-custom-item="true"
                            :items="signatureFonts"
                            @input="updateFont"
                        >
                            <template #selection="{ item }">
                                <div class="font-selection" :style="{ 'font-family': item.name }">
                                    {{ textSignature }}
                                </div>
                            </template>
                            <template #item="{ item }">
                                <div class="font-item-option" :style="{ 'font-family': item.name }">
                                    {{ textSignature }}
                                </div>
                            </template>
                        </app-select-dropdown>
                        <app-button v-if="selectedTab === 'draw'" @click="clearCanvas">
                            Clear
                        </app-button>
                        <div v-if="selectedTab !== 'upload'" class="color-select">
                            <div
                                v-for="color in signatureColors"
                                :key="color.id"
                                :class="colorIndicatorClasses(color)"
                                :style="{ background: color.value }"
                                @click="updateColor(color)"
                            >
                                <r3-checkmark-svg v-if="isActiveColor(color)" />
                            </div>
                        </div>
                    </div>
                    <div v-cloak v-show="selectedTab === 'draw'" id="signature-container">
                        <canvas id="draw-canvas" ref="drawcanvas">
                            Sorry this browser does not support HTML 5 Canvas!
                        </canvas>
                    </div>
                    <app-input
                        v-show="selectedTab === 'text'"
                        ref="signatureInput"
                        v-model="textSignature"
                        :style="{
                            color: selectedColor.value,
                            'font-family': selectedFont.name,
                        }"
                        @blur="resetSignature"
                        @focus="startTyping"
                    />
                    <app-drag-and-drop-area
                        v-show="selectedTab === 'upload'"
                        v-model="signatureUrl"
                        type="image"
                        label="Or Drag Image Here"
                        size="small"
                        :file-accept="fileConfig.fileSelectorAllowedTypes"
                        :is-library-enabled="false"
                        @upload="handleUpload"
                        @remove="handleRemove"
                    >
                        <template #upload-icon>
                            <f6-upload-cloud-svg />
                        </template>
                        <template #library-icon>
                            <r21-library-outline-svg />
                        </template>
                    </app-drag-and-drop-area>
                </div>
            </div>
            <p v-if="hasError" class="signature-error">Please add a signature to proceed</p>
            <div class="disclaimer">
                I understand that GlassHive uses my name, email address and limited information will
                be used to complete the signature process and to enhance the user experience. To
                learn more about how GlassHive uses information, please see our
                <router-link
                    :to="`${baseUrl}/Document/Sign/${documentGuid}/disclosure`"
                    target="_blank"
                >
                    Privacy Policy
                </router-link>
                . By signing this document with an electronic signature, I agree that such signature
                will be as valid as handwritten signatures and considered originals to the extent
                allowed by applicable law.
            </div>
        </div>
        <div class="widget-footer">
            <div class="cancel-container">
                <app-button class="back-button" @click="closeWidget"> Cancel </app-button>
            </div>
            <div class="action-container">
                <app-button :disabled="loading" :loading="loading" @click="handleSign">
                    <span v-if="loading">Signing...</span>
                    <span v-else> Accept and Sign </span>
                </app-button>
            </div>
        </div>
    </div>
</template>

<script>
const AppButton = () => import('@/components/common/app-button');
const AppDragAndDropArea = () => import('@/components/common/app-drag-and-drop-area');
const AppInput = () => import('@/components/common/app-input');
const AppSelectDropdown = () => import('@/components/common/app-select-dropdown');
const AppTab = () => import('@/components/common/app-tab');
const F6UploadCloudSvg = () => import('@/components/svg/grid/f6-upload-cloud-svg');
const R3CheckmarkSvg = () => import('@/components/svg/grid/r3-checkmark-svg');
const R21LibraryOutlineSvg = () => import('@/components/svg/grid/r21-library-outline-svg');
const WidgetCloseSvg = () => import('@/components/svg/quick-add/widget-close-svg.vue');

import EventBus from '@/scripts/event-bus';
import { DocumentService } from '@/scripts/services/document/document-service';
import FileMixin, { FileTypeError, FileByteSizeLimitError } from '@/mixins/file-mixin';
import { isNil, noop } from 'lodash';
import SignaturePad from 'signature_pad';
import { LogLevel } from '@/scripts/logger';

export default {
    name: 'document-signature-widget',

    components: {
        'app-button': AppButton,
        'app-drag-and-drop-area': AppDragAndDropArea,
        'app-input': AppInput,
        'app-select-dropdown': AppSelectDropdown,
        'app-tab': AppTab,
        'f6-upload-cloud-svg': F6UploadCloudSvg,
        'r3-checkmark-svg': R3CheckmarkSvg,
        'r21-library-outline-svg': R21LibraryOutlineSvg,
        'widget-close-svg': WidgetCloseSvg,
    },

    mixins: [FileMixin],

    props: {
        widget: {
            type: Object,
            required: true,
        },

        options: {
            type: Object,
            default: () => ({}),
        },
    },

    data() {
        return {
            loading: false,
            hasError: false,
            currentTab: null,
            signaturePad: null,
            textSignature: this.options.recipientName,
            signatureUrl: null,
            selectedColor: null,
            selectedFont: null,

            views: [
                {
                    id: 0,
                    name: 'Draw',
                    disabled: false,
                    value: 'draw',
                },
                {
                    id: 1,
                    name: 'Type',
                    disabled: false,
                    value: 'text',
                },
                {
                    id: 2,
                    name: 'Upload',
                    disabled: false,
                    value: 'upload',
                },
            ],

            signatureColors: [
                {
                    id: 0,
                    value: '#382153',
                },
                {
                    id: 1,
                    value: '#5052FE',
                },
                {
                    id: 2,
                    value: '#AF1E69',
                },
            ],

            signatureFonts: [
                {
                    id: 0,
                    name: 'Roboto',
                },
                {
                    id: 1,
                    name: 'Dancing Script',
                },
                {
                    id: 2,
                    name: 'Solitreo',
                },
                {
                    id: 3,
                    name: 'Great Vibes',
                },
                {
                    id: 4,
                    name: 'Caveat',
                },
            ],

            fileConfig: {
                byteSizeLimit: 2097152,
                fileSelectorAllowedTypes: ['jpeg', 'jpg', 'png', 'svg'],
                allowedFileTypes: ['image/png', 'image/svg+xml', 'image/jpg', 'image/jpeg'],
            },
        };
    },

    computed: {
        baseUrl() {
            return this.$store.getters.baseUrl;
        },

        fieldId() {
            return this.options.fieldId;
        },

        selectedTab() {
            return this.currentTab.value;
        },

        documentGuid() {
            return this.$route.params.guid;
        },
    },

    watch: {
        selectedColor: {
            handler({ value }) {
                this.signaturePad.penColor = value;
            },
        },
    },

    created() {
        this.currentTab = this.views[0];
        this.selectedColor = this.signatureColors[0];
        this.selectedFont = this.signatureFonts[0];
    },

    mounted() {
        window.addEventListener('resize', this.resizeCanvas);
        this.initSignaturePad();
        this.resizeCanvas();
    },

    beforeUnmount() {
        window.removeEventListener('resize', this.resizeCanvas);
    },

    methods: {
        // Tab Navigation Methods
        isActiveTab(view) {
            return this.currentTab.id === view.id;
        },

        setActiveTab(view) {
            this.currentTab = view;
            this.hasError = false;
        },

        // Canvas Methods
        initSignaturePad() {
            this.drawingCanvas = this.$refs.drawcanvas;
            this.drawingCanvas.getContext('2d', { willReadFrequently: true });
            this.signaturePad = new SignaturePad(this.drawingCanvas, {
                minWidth: 0.75,
                maxWidth: 1.5,
            });

            this.signaturePad.addEventListener(
                'beginStroke',
                () => {
                    this.resetSignature();
                },
                { once: true }
            );
        },

        resizeCanvas() {
            const ratio = Math.max(window.devicePixelRatio || 1, 1);
            this.drawingCanvas.width = this.drawingCanvas.offsetWidth * ratio;
            this.drawingCanvas.height = this.drawingCanvas.offsetHeight * ratio;
            this.drawingCanvas.getContext('2d', { willReadFrequently: true }).scale(ratio, ratio);
            this.signaturePad.clear();
        },

        isBlankCanvas(canvas) {
            const newCtx = canvas.getContext('2d');
            const pixelBuffer = new Uint32Array(
                newCtx.getImageData(0, 0, canvas.width, canvas.height).data.buffer
            );
            return !pixelBuffer.some((color) => color !== 0);
        },

        // AutoCrop function to retrieve only the content
        cropCanvas(canvas, buffer = 5) {
            const cropCtx = canvas.getContext('2d');
            const imageData = cropCtx.getImageData(0, 0, canvas.width, canvas.height);
            const { width: w, height: h } = canvas;
            let x, y, index;
            const pix = { x: [], y: [] };
            for (y = 0; y < h; y++) {
                for (x = 0; x < w; x++) {
                    index = (y * w + x) * 4;
                    if (imageData.data[index + 3] > 0) {
                        pix.x.push(x);
                        pix.y.push(y);
                    }
                }
            }

            const minX = Math.max(0, Math.min(...pix.x) - buffer);
            const maxX = Math.min(w - 1, Math.max(...pix.x) + buffer);
            const minY = Math.max(0, Math.min(...pix.y) - buffer);
            const maxY = Math.min(h - 1, Math.max(...pix.y) + buffer);

            const cutW = maxX - minX;
            const cutH = maxY - minY;
            const cut = cropCtx.getImageData(minX, minY, cutW, cutH);
            const croppedCanvas = document.createElement('canvas');
            croppedCanvas.width = cutW;
            croppedCanvas.height = cutH;
            const croppedCtx = croppedCanvas.getContext('2d');
            croppedCtx.putImageData(cut, 0, 0);

            return croppedCanvas;
        },

        clearCanvas() {
            this.signaturePad.clear();
        },

        // Typed signature methods
        startTyping() {
            this.hasError = false;
        },

        resetSignature() {
            this.hasError = false;
        },

        createTextSignature(scaleFactor = 1) {
            const textCanvas = document.createElement('canvas');
            const ctx2 = textCanvas.getContext('2d', { willReadFrequently: true });

            ctx2.font = `60px ${this.selectedFont ? this.selectedFont.name : 'Roboto'}`;
            const textWidth = ctx2.measureText(this.textSignature).width;

            textCanvas.width = Math.ceil(textWidth + 20) * scaleFactor;
            textCanvas.height = Math.ceil(300 * scaleFactor);

            ctx2.clearRect(0, 0, textCanvas.width, textCanvas.height);
            ctx2.fillStyle = this.selectedColor.value;
            ctx2.textAlign = 'center';
            ctx2.textBaseline = 'middle';
            ctx2.font = `${60 * scaleFactor}px ${
                this.selectedFont ? this.selectedFont.name : 'Roboto'
            }`;
            ctx2.fillText(this.textSignature, textCanvas.width / 2, textCanvas.height / 2);

            const croppedSignature = this.cropCanvas(textCanvas);
            ctx2.clearRect(0, 0, textCanvas.width, textCanvas.height);
            return croppedSignature;
        },

        updateFont(value) {
            this.selectedFont = value;
        },

        updateColor(color) {
            this.selectedColor = color;
            if (this.selectedTab === 'draw' && !this.signaturePad.isEmpty()) {
                const curvesData = this.signaturePad.toData();
                curvesData.forEach((curve) => {
                    curve.penColor = color.value;
                });
                this.signaturePad.fromData(curvesData);
            }
        },

        isActiveColor(value) {
            return this.selectedColor.id === value.id;
        },

        colorIndicatorClasses(value) {
            return ['color-indicator', { active: this.isActiveColor(value) }];
        },

        // Uploaded signature methods
        handleUpload(e) {
            this.getBase64Url(e, this.fileConfig)
                .then(({ encodingStr }) => {
                    this.signatureUrl = encodingStr;
                    this.hasError = false;
                })
                .catch((error) => {
                    if (error instanceof FileByteSizeLimitError) {
                        this.$helpers.showFailNotification(
                            'File size exceeds the maximum amount of 2Mbs',
                            { level: LogLevel.INFO },
                        );
                    } else if (error instanceof FileTypeError) {
                        const [head, ...tail] = this.fileConfig.fileSelectorAllowedTypes;
                        this.$helpers.showFailNotification(
                            `File needs to be of type ${tail.join(', ')} or ${head}`,
                            { level: LogLevel.INFO },
                        );
                    } else {
                        this.$helpers.showFailNotification(
                            'An error occurred while uploading your image. Please check your format and try again.',
                            { error },
                        );
                    }
                });
        },

        handleRemove() {
            this.signatureUrl = null;
        },

        // Saving Signature Mehthods
        processDrawnSignature() {
            let isBlank = this.isBlankCanvas(this.drawingCanvas);
            if (isBlank) {
                this.hasError = true;
            } else {
                const croppedCanvas = this.cropCanvas(this.drawingCanvas);
                const canvasImageUrl = croppedCanvas.toDataURL('image/png', 1.0);
                this.saveSignature(canvasImageUrl);
            }
        },

        processTypedSignature() {
            let textSignature = this.$refs.signatureInput.value;
            if (!textSignature) {
                this.hasError = true;
            } else {
                const textSignature = this.createTextSignature(2);
                const canvasImageUrl = textSignature.toDataURL('image/png', 1.0);
                this.saveSignature(canvasImageUrl);
            }
        },

        processUploadSignature() {
            let isEmpty = isNil(this.signatureUrl);
            if (isEmpty) {
                this.hasError = true;
            } else {
                this.saveSignature(this.signatureUrl);
            }
        },

        handleSign() {
            switch (this.selectedTab) {
                case 'draw':
                    this.processDrawnSignature();
                    return;
                case 'text':
                    this.processTypedSignature();
                    return;
                case 'upload':
                    this.processUploadSignature();
                    return;
                default:
                    noop();
            }
        },

        saveSignature(signature) {
            this.loading = true;

            const file = {
                Name: `signature-${this.documentGuid}`,
                Url: signature,
                RecipientGuid: this.options.guid,
            };

            DocumentService.postFile(file)
                .then(({ data }) => {
                    EventBus.$emit(`input-${this.fieldId}:sign`, data);
                    EventBus.$emit('document-field:sign', data);
                    this.closeWidget();
                })
                .catch((error) => {
                    this.$helpers.showFailNotification('Error trying to save your signature', {
                        error,
                    });
                })
                .finally(() => {
                    this.loading = false;
                });
        },

        closeWidget() {
            EventBus.$emit('close-widget', this.widget);
        },
    },
};
</script>

<style>
/* Signature Fonts */
@import url('https://fonts.googleapis.com/css2?family=Caveat&family=Dancing+Script&family=Great+Vibes&family=Solitreo&display=swap');

.document-signature-widget .content-wrapper {
    padding: 24px;
}

.document-signature-widget .content-wrapper .tabs-container {
    border-bottom: 2px solid #e9e9ef;
    display: flex;
    height: 48px;
    justify-content: flex-start;
}

.document-signature-widget .content-wrapper .tabs-container .app-tab {
    width: 120px;
    padding: 12px;
}

.document-signature-widget .document-signing-card {
    background-color: #f7f7f8;
    border-radius: 12px;
    margin: 15px 0;
    padding: 15px;
}

.document-signature-widget .document-signing-card .settings-container {
    align-items: center;
    display: flex;
    justify-content: space-between;
    margin-bottom: 8px;
}

.document-signature-widget .document-signing-card .settings-container .app-select-dropdown {
    width: 50%;
}

.document-signature-widget
    .document-signing-card
    .settings-container
    .app-select-dropdown
    .font-selection {
    font-size: 24px;
    line-height: 1.5em;
    min-height: 1.5em;
    max-width: 100%;
    overflow: hidden;
    text-align: center;
    text-overflow: ellipsis;
    white-space: nowrap;
    width: 100%;
}

.document-signature-widget
    .document-signing-card
    .settings-container
    .app-select-dropdown
    .font-item-option {
    font-size: 24px;
    line-height: 1.5em;
    min-height: 1.5em;
    padding: 4px 0;
}

.document-signature-widget
    .document-signing-card
    .settings-container
    .v-text-field.v-text-field--enclosed
    .v-text-field__details {
    display: none;
}

.document-signature-widget
    .document-signing-card
    .settings-container
    .app-select.v-input
    > .v-input__control
    > .v-input__slot {
    margin-bottom: 0;
    min-height: 40px;
}

.document-signature-widget .document-signing-card .settings-container .color-select {
    align-items: center;
    display: flex;
    gap: 12px;
    height: 40px;
}

.document-signature-widget .document-signing-card .settings-container .color-indicator {
    border-radius: 50%;
    cursor: pointer;
    display: flex;
    height: 24px;
    width: 24px;
    transition: all 100ms ease;
}

.document-signature-widget .document-signing-card .settings-container .color-indicator.active {
    cursor: auto;
    height: 32px;
    width: 32px;
}

.document-signature-widget .document-signing-card .settings-container .color-indicator.active svg {
    margin: auto;
    height: auto;
    width: 16px;
}

.document-signature-widget
    .document-signing-card
    .settings-container
    .color-indicator.active
    svg
    .a {
    fill: #ffffff;
}

.document-signature-widget .document-signing-card .app-input input {
    background-color: transparent;
    border: initial;
    border-bottom: 1px solid #000000;
    border-radius: 0;
    color: inherit;
    font-family: inherit;
    font-size: clamp(30px, 10vw, 60px);
    height: 200px;
    text-align: center;
}

.document-signature-widget .document-signing-card #signature-container {
    border-bottom: 1px solid #382153;
    height: 200px;
    width: 100%;
    position: relative;
}

.document-signature-widget .document-signing-card #draw-canvas {
    width: 100%;
    height: 100%;
}

.document-signature-widget
    .document-signing-card
    .app-drag-and-drop-area-preview
    .content-container
    img {
    max-width: initial;
}

.document-signature-widget
    .document-signing-card
    .app-drag-and-drop-area
    .app-drag-and-drop-area-uploader
    .button-container
    .app-button:last-child {
    display: none;
}

.document-signature-widget .content-wrapper .signature-error {
    font: 400 12px 'Roboto';
    color: #f30070;
    margin-bottom: 12px;
}

.document-signature-widget .content-wrapper .disclaimer {
    color: #707070;
    font: 400 14px 'Roboto';
    text-align: justify;
}

.document-signature-widget .content-wrapper .disclaimer a {
    color: #428bca;
    text-decoration: none;
}

@media only screen and (max-width: 768px) {
    .document-signature-widget.widget .content-wrapper {
        padding: 24px 16px;
    }

    .document-signature-widget
        .document-signing-card
        .settings-container
        .app-select-dropdown
        .font-selection {
        font-size: 14px;
    }
}
</style>
